import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type QueryClient,
} from "@tanstack/react-query";
import { useSnackbar } from "notistack";

import { withJWTClient } from "../connectClient";
import {
  emptyToUndefined,
  gatewayUrl,
  isAlreadyExistsError,
  withUpdateMask,
} from "../gateway";
import type { ComponentDefinition } from "grapesjs";

import { emailPreviewKeys, invalidateFramePreviews } from "@/api/dragon-tiles";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

// --- Types ---
export type CreativeBlockType = "header" | "footer";
export type CreativeBlockSubtype = "default" | "minimal";
export type CreativeBlockMedium = "email" | "sms" | "mms" | "rcs";

export interface CreativeBlock {
  id: string;
  name: string;
  component_data: ComponentDefinition;
  preview_html: string;
  /**
   * Minified head <style> blocks (media-query/responsive, e.g. .mj-column-px-540)
   * used when embedding blocks. May be absent for older blocks.
   */
  styles?: string;
  site: string;
  create_date: string;
  modify_date: string;
  /** Role this block fills for the site (e.g. the header). Unset for plain blocks. */
  type?: CreativeBlockType;
  /** At most one live block per site + medium + type holds a given subtype. */
  subtype?: CreativeBlockSubtype;
  /** Channel the block is for; connect-api defaults to email. */
  medium?: CreativeBlockMedium;
  /** Live tiles using the block. The list API omits it when 0. */
  frame_count?: number;
}

export interface GetCreativeBlocksResponse {
  blocks: CreativeBlock[];
}

export interface CreateCreativeBlockPayload {
  name: string;
  component_data: ComponentDefinition;
  preview_html: string;
  styles?: string;
  /** Defaults to email on the API when omitted. */
  medium?: CreativeBlockMedium;
}

export interface UpdateCreativeBlockPayload {
  name?: string;
  component_data?: ComponentDefinition;
  preview_html?: string;
  styles?: string;
  /** null clears the type. */
  type?: CreativeBlockType | null;
  /** Only an edit can set a subtype; clearing it directly is rejected by the API. */
  subtype?: CreativeBlockSubtype;
  medium?: CreativeBlockMedium;
}

export interface CreativeBlockFrame {
  id: string;
  name: string;
}

export interface CreativeBlockFramesResponse {
  count: number;
  frames: CreativeBlockFrame[];
}

interface GatewayBlock {
  id?: string;
  rj_id?: string;
  site_id?: string;
  rj_site_id?: string;
  name: string;
  component_data: ComponentDefinition | null;
  preview_html: string;
  styles: string;
  created_at: string | null;
  updated_at: string | null;
  /** Proto enum names, e.g. "BLOCK_TYPE_HEADER"; "*_UNSPECIFIED" means unset. */
  type?: string;
  subtype?: string;
  medium?: string;
  frame_count?: number;
}

interface GatewayBlocksResponse {
  blocks: GatewayBlock[];
}

interface GatewayBlockFrameRef {
  id?: string;
  rj_id?: string;
  name: string;
}

interface GatewayBlockFramesResponse {
  count: number;
  frames: GatewayBlockFrameRef[];
}

/** "BLOCK_TYPE_HEADER" -> "header"; unset or "*_UNSPECIFIED" -> undefined. */
function fromGatewayEnum<T extends string>(
  value: string | undefined,
  prefix: string,
): T | undefined {
  if (!value || !value.startsWith(prefix)) return undefined;
  const name = value.slice(prefix.length).toLowerCase();
  return name === "unspecified" ? undefined : (name as T);
}

/** "header" -> "BLOCK_TYPE_HEADER"; null -> "BLOCK_TYPE_UNSPECIFIED" (clears it). */
function toGatewayEnum(
  value: string | null | undefined,
  prefix: string,
): string | undefined {
  if (value === undefined) return undefined;
  return `${prefix}${(value ?? "unspecified").toUpperCase()}`;
}

function fromGatewayBlock(block: GatewayBlock): CreativeBlock {
  return {
    id: block.rj_id || block.id || "",
    name: block.name,
    component_data: block.component_data ?? {},
    preview_html: block.preview_html,
    styles: emptyToUndefined(block.styles),
    site: block.rj_site_id || block.site_id || "",
    create_date: block.created_at ?? "",
    modify_date: block.updated_at ?? "",
    type: fromGatewayEnum<CreativeBlockType>(block.type, "BLOCK_TYPE_"),
    subtype: fromGatewayEnum<CreativeBlockSubtype>(
      block.subtype,
      "BLOCK_SUBTYPE_",
    ),
    medium: fromGatewayEnum<CreativeBlockMedium>(block.medium, "MEDIUM_TYPE_"),
    frame_count: block.frame_count,
  };
}

function toGatewayCreatePayload(payload: CreateCreativeBlockPayload) {
  return {
    ...payload,
    medium: toGatewayEnum(payload.medium, "MEDIUM_TYPE_"),
  };
}

function toGatewayUpdatePayload(payload: UpdateCreativeBlockPayload) {
  return {
    ...payload,
    type: toGatewayEnum(payload.type, "BLOCK_TYPE_"),
    subtype: toGatewayEnum(payload.subtype, "BLOCK_SUBTYPE_"),
    medium: toGatewayEnum(payload.medium, "MEDIUM_TYPE_"),
  };
}

// --- API Functions ---
export async function getCreativeBlocks(
  siteId: string,
): Promise<GetCreativeBlocksResponse> {
  const response = await withJWTClient((client) =>
    client.get<GatewayBlocksResponse>(gatewayUrl(siteId, "blocks")),
  );
  return { blocks: response.data.blocks.map(fromGatewayBlock) };
}

export async function getCreativeBlock(
  siteId: string,
  blockId: string,
): Promise<CreativeBlock> {
  const response = await withJWTClient((client) =>
    client.get<GatewayBlock>(gatewayUrl(siteId, "blocks", blockId)),
  );
  return fromGatewayBlock(response.data);
}

export async function createCreativeBlock(
  siteId: string,
  payload: CreateCreativeBlockPayload,
): Promise<CreativeBlock> {
  const body = toGatewayCreatePayload(payload);
  const response = await withJWTClient((client) =>
    client.post<GatewayBlock>(gatewayUrl(siteId, "blocks"), body),
  );
  return fromGatewayBlock(response.data);
}

export async function updateCreativeBlock(
  siteId: string,
  blockId: string,
  payload: UpdateCreativeBlockPayload,
): Promise<CreativeBlock> {
  const body = toGatewayUpdatePayload(payload);
  const response = await withJWTClient((client) =>
    client.patch<GatewayBlock>(
      withUpdateMask(gatewayUrl(siteId, "blocks", blockId), body),
      body,
    ),
  );
  return fromGatewayBlock(response.data);
}

export async function deleteCreativeBlock(
  siteId: string,
  blockId: string,
): Promise<void> {
  await withJWTClient((client) =>
    client.delete(gatewayUrl(siteId, "blocks", blockId)),
  );
}

export async function getCreativeBlockFrames(
  siteId: string,
  blockId: string,
): Promise<CreativeBlockFramesResponse> {
  const response = await withJWTClient((client) =>
    client.get<GatewayBlockFramesResponse>(
      gatewayUrl(siteId, "blocks", blockId, "frames"),
    ),
  );
  return {
    count: response.data.count,
    frames: response.data.frames.map((frame) => ({
      id: frame.rj_id || frame.id || "",
      name: frame.name,
    })),
  };
}

// --- Query Keys ---
export const creativeBlocksKeys = {
  all: (siteId: string) => ["creative-blocks", siteId] as const,
  detail: (siteId: string, blockId: string) =>
    ["creative-blocks", siteId, blockId] as const,
  framesPrefix: (siteId: string) =>
    ["creative-blocks", siteId, "frames"] as const,
  frames: (siteId: string, blockId: string) =>
    ["creative-blocks", siteId, "frames", blockId] as const,
};

export function invalidateFramePreviewQueries(
  queryClient: QueryClient,
  siteId: string,
) {
  void queryClient.invalidateQueries({
    queryKey: ["bulkFrameVariantPreviews", siteId],
  });
  // Per-variant previews are seeded imperatively via setQueryData with no queryFn
  // (see seedFrameVariantPreviewCache). invalidateQueries is a no-op for them — it
  // marks the entry stale but keeps the data and there is no query to refetch it, so
  // the creative-sphere tile list (useFramePreviews) keeps reading the old preview
  // and treats the id as "cached". Remove the entries instead so the list sees them
  // as uncached and refetches fresh previews (via the bulk query) on next mount.
  queryClient.removeQueries({
    queryKey: ["frameVariantPreview", siteId],
  });
  invalidateFramePreviews(queryClient, siteId);
  void queryClient.invalidateQueries({
    queryKey: ["framesWithPreviews", siteId],
  });
  void queryClient.invalidateQueries({
    queryKey: emailPreviewKeys.all(siteId),
  });
  void queryClient.invalidateQueries({
    queryKey: ["frame", siteId],
  });
  void queryClient.invalidateQueries({
    queryKey: ["bulkEmailBroadcastPreviews", siteId],
  });
}

// --- Query Options ---
export function creativeBlocksQueryOptions(siteId: string) {
  return {
    queryKey: creativeBlocksKeys.all(siteId),
    queryFn: () => getCreativeBlocks(siteId),
  };
}

export function creativeBlockQueryOptions(siteId: string, blockId: string) {
  return {
    queryKey: creativeBlocksKeys.detail(siteId, blockId),
    queryFn: () => getCreativeBlock(siteId, blockId),
  };
}

// --- Tanstack Query Hooks ---
export function useCreativeBlocks(
  siteId: string,
  options?: Omit<
    UseQueryOptions<GetCreativeBlocksResponse, Error>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: creativeBlocksKeys.all(siteId),
    queryFn: () => getCreativeBlocks(siteId),
    enabled: !!siteId,
    ...options,
  });
}

export function useCreativeBlock(
  siteId: string,
  blockId: string,
  options?: Omit<UseQueryOptions<CreativeBlock, Error>, "queryKey" | "queryFn">,
) {
  return useQuery({
    queryKey: creativeBlocksKeys.detail(siteId, blockId),
    queryFn: () => getCreativeBlock(siteId, blockId),
    enabled: !!siteId && !!blockId,
    ...options,
  });
}

export function useCreativeBlockFrames(
  siteId: string | undefined,
  blockId: string | undefined,
  options?: Omit<
    UseQueryOptions<CreativeBlockFramesResponse, Error>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: creativeBlocksKeys.frames(siteId ?? "", blockId ?? ""),
    // Returns the frames/tiles where this saved block is used.
    queryFn: () => getCreativeBlockFrames(siteId!, blockId!),
    enabled: !!siteId && !!blockId,
    ...options,
  });
}

export function useCreateCreativeBlock() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<
    CreativeBlock,
    Error,
    { siteId: string; payload: CreateCreativeBlockPayload }
  >({
    mutationFn: ({ siteId, payload }) => createCreativeBlock(siteId, payload),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: creativeBlocksKeys.all(variables.siteId),
      });
      invalidateFramePreviewQueries(queryClient, variables.siteId);
      enqueueSnackbar("Block saved successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      if (isAlreadyExistsError(error)) {
        enqueueSnackbar(
          "A block with this name already exists. Please choose a different name.",
          {
            variant: "error",
          },
        );
      } else {
        enqueueErrorSnackbar(error, "Failed to save block");
      }
    },
  });
}

export function useUpdateCreativeBlock() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<
    CreativeBlock,
    Error,
    { siteId: string; blockId: string; payload: UpdateCreativeBlockPayload }
  >({
    mutationFn: ({ siteId, blockId, payload }) =>
      updateCreativeBlock(siteId, blockId, payload),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: creativeBlocksKeys.all(variables.siteId),
      });
      invalidateFramePreviewQueries(queryClient, variables.siteId);
      enqueueSnackbar("Block updated successfully", {
        variant: "success",
      });
    },
    onError: (error, variables) => {
      enqueueErrorSnackbar(error, "Failed to update block", {
        onRetry: () => {
          // Retry the mutation with the same variables
          void updateCreativeBlock(
            variables.siteId,
            variables.blockId,
            variables.payload,
          );
        },
      });
    },
  });
}

export function useDeleteCreativeBlock() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<void, Error, { siteId: string; blockId: string }>({
    mutationFn: ({ siteId, blockId }) => deleteCreativeBlock(siteId, blockId),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: creativeBlocksKeys.all(variables.siteId),
      });
      invalidateFramePreviewQueries(queryClient, variables.siteId);
      enqueueSnackbar("Block deleted successfully", {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueErrorSnackbar(error, "Failed to delete block");
    },
  });
}
